import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const navItems = [
  { label: "Home", href: "/" },
  { label: "Über Uns", href: "/ueber-uns" },
  { label: "Produkte", href: "/produkte" },
  { label: "Service", href: "/service" },
  { label: "Regionales", href: "/regionales" },
  { label: "Karriere", href: "/karriere" },
];

const ReweNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Escape schliesst das Menue, Hintergrund-Scroll wird gesperrt
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-background/90 backdrop-blur-md border-b border-border"
            : "bg-transparent"
        }`}
        role="navigation"
        aria-label="Hauptnavigation"
      >
        <div className="flex items-center justify-between px-6 md:px-16 py-5">
          <Link
            to="/"
            className="font-display text-2xl font-bold tracking-tight text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background rounded"
            aria-label="REWE Markt – Startseite"
          >
            <span className="text-primary-bright">REWE</span>
            <span className="text-muted-foreground text-sm font-body ml-2 hidden sm:inline">
              Ihr Markt
            </span>
          </Link>

          {/* Desktop nav */}
          <ul className="hidden lg:flex items-center gap-8 list-none m-0 p-0">
            {navItems.map((item) => (
              <li key={item.label}>
              <Link
                to={item.href}
                className={`text-sm font-body transition-colors duration-300 relative group focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background rounded px-1 ${
                  location.pathname === item.href
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                aria-current={location.pathname === item.href ? "page" : undefined}
              >
                {item.label}
                <span
                  className={`absolute -bottom-1 left-0 h-[1px] bg-primary transition-all duration-300 ${
                    location.pathname === item.href ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
              </li>
            ))}
          </ul>

          {/* Mobile menu button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background rounded min-h-11 min-w-11 flex items-center justify-center"
            aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            id="mobile-menu"
            className="fixed inset-0 z-40 bg-background/98 backdrop-blur-lg flex flex-col items-center justify-center gap-8"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
          >
            <ul className="flex flex-col items-center gap-8 list-none m-0 p-0">
            {navItems.map((item, i) => (
              <motion.li
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
              >
                <Link
                  to={item.href}
                  className={`font-display text-3xl transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded px-2 ${
                    location.pathname === item.href
                      ? "text-primary-bright"
                      : "text-foreground hover:text-primary-bright"
                  }`}
                  aria-current={location.pathname === item.href ? "page" : undefined}
                >
                  {item.label}
                </Link>
              </motion.li>
            ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ReweNav;